import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Heart, ArrowLeft, Star } from "lucide-react";
import { toast } from "sonner";
import { API, inr, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useOutlet } from "../context/OutletContext";
import ProductCard from "../components/ProductCard";
import PostMedia from "../components/PostMedia";
import Seo from "../components/Seo";
import Breadcrumbs, { breadcrumbLd } from "../components/Breadcrumbs";
import { SITE_URL } from "../lib/seo";

export default function ProductDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { refresh } = useCart();
  const { outlet } = useOutlet();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [wished, setWished] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [looks, setLooks] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [tab, setTab] = useState("about");

  useEffect(() => {
    setSelectedVariant(null); setActiveImg(0);
    API.get(`/products/${id}${outlet ? `?outlet=${outlet.id}` : ""}`).then((r) => {
      setData(r.data);
      if (r.data.product?.id) {
        API.get(`/community/posts?product_id=${r.data.product.id}&limit=12`).then((x) => setLooks(x.data)).catch(() => {});
        API.get(`/community/posts?product_id=${r.data.product.id}&type=review&limit=12`).then((x) => setReviews(x.data)).catch(() => {});
      }
    }).catch(() => setData({ product: null, related: [] }));
  }, [id, outlet]);

  useEffect(() => {
    const p = data?.product;
    if (p && p.slug && p.slug !== id && id === p.id) {
      nav(`/product/${p.slug}`, { replace: true });
    }
  }, [data, id, nav]);

  if (!data) return <div className="max-w-7xl mx-auto px-4 py-20 text-stone-500">Loading…</div>;
  if (!data.product) return (
    <div className="max-w-7xl mx-auto px-4 py-20 text-stone-500" data-testid="product-not-found">
      <Seo title="Product Not Found | Suvikka" noindex />
      Product not found.
    </div>
  );

  const p = data.product;
  const activeVariants = (p.variants || []).filter((v) => (v.status || "active") === "active");
  const hasShades = !!p.has_variants && activeVariants.length > 0;
  const unitPrice = selectedVariant?.price ? selectedVariant.price : p.selling_price;
  const variantSku = selectedVariant?.sku || p.sku;
  const mainImg = selectedVariant?.image || p.images?.[activeImg] || p.images?.[0] || "";
  const shadeOos = hasShades && selectedVariant && Number(selectedVariant.stock) <= 0;
  const off = p.mrp > unitPrice ? Math.round((1 - unitPrice / p.mrp) * 100) : 0;
  const whyPoints = [
    p.club_price != null ? `Members pay just ${inr(p.club_price)}` : null,
    p.wallet_reward > 0 ? `Earn ${inr(p.wallet_reward)} back in your Suvikka Wallet` : null,
    hasShades ? `Available in ${activeVariants.length} beautiful shade${activeVariants.length > 1 ? "s" : ""}` : null,
    "Honest pricing, loved by the Suvikka community",
    "Ships across India with care",
  ].filter(Boolean);
  const PTABS = [["about", "About This Product"], ["why", "Why She'll Love It"], ["details", "Details"]];
  const oos = outlet && p.outlet_in_stock === false;
  const handle = p.slug || p.id;
  const canonicalPath = `/product/${handle}`;
  const availability = (outlet ? p.outlet_in_stock : p.stock_status === "In Stock") ? "https://schema.org/InStock" : "https://schema.org/OutOfStock";
  const crumbs = [{ name: "Home", path: "/" }, { name: "Shop", path: "/products" }, { name: p.category }, { name: p.title }];
  const productLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: p.title,
    description: p.description || `${p.title} — available at Suvikka.`,
    image: (p.images || []).map((x) => (x?.startsWith("http") ? x : `${SITE_URL}${x || ""}`)),
    sku: p.sku || undefined,
    category: p.category,
    brand: { "@type": "Brand", name: "Suvikka" },
    offers: {
      "@type": "Offer",
      price: p.selling_price,
      priceCurrency: "INR",
      availability,
      url: `${SITE_URL}${canonicalPath}`,
    },
  };

  const wish = async () => {
    if (!user) { nav("/login"); return; }
    try {
      if (wished) { await API.delete(`/wishlist/${p.id}`); setWished(false); }
      else { await API.post(`/wishlist/${p.id}`); setWished(true); toast.success("Added to wishlist"); }
    } catch { toast.error("Could not update wishlist"); }
  };

  const addToBag = async () => {
    if (oos) return;
    if (!user) { nav("/login"); return; }
    if (hasShades && !selectedVariant) { toast.error("Please select a shade before adding this product to your cart."); return; }
    if (selectedVariant && Number(selectedVariant.stock) <= 0) { toast.error("This shade is out of stock"); return; }
    try {
      const q = new URLSearchParams();
      if (outlet) q.set("outlet", outlet.id);
      if (selectedVariant) q.set("variant_id", selectedVariant.id);
      const qs = q.toString();
      await API.post(`/cart/${p.id}${qs ? `?${qs}` : ""}`); await refresh();
      toast.success(selectedVariant ? `Added — ${selectedVariant.name}` : "Added to bag");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid="product-detail-page">
      <Seo title={p.meta_title || `${p.title} — ${p.category} | Suvikka`} description={p.meta_description || (p.description ? p.description.slice(0, 155) : `Shop ${p.title} at Suvikka — ${p.category} at honest prices, available across India.`)} canonicalPath={canonicalPath} type="product" image={p.images?.[0]} jsonLd={[productLd, breadcrumbLd(crumbs)]} />
      <div className="mb-5"><Breadcrumbs items={crumbs} /></div>
      <Link to="/products" className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-900" data-testid="back-to-products"><ArrowLeft className="w-4 h-4" /> Back to products</Link>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mt-6">
        <div>
          <div className="aspect-[4/5] bg-stone-100 overflow-hidden rounded-2xl shadow-warm">
            <img src={mainImg} alt={p.title} className="w-full h-full object-cover" data-testid="product-main-image" />
          </div>
          {p.images?.length > 1 && (
            <div className="flex gap-3 mt-3">
              {p.images.map((img, i) => (
                <button key={i} data-testid={`product-thumb-${i}`} onClick={() => setActiveImg(i)}
                  className={`w-20 aspect-square overflow-hidden border-2 ${i === activeImg ? "border-[#6A1B29]" : "border-transparent"}`}>
                  <img src={img} alt={`${p.title} view ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#6A1B29]" data-testid="product-category">{p.category}</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-stone-900 mt-2" data-testid="product-title">{p.title}</h1>
          <div className="flex items-baseline gap-3 mt-4">
            <span className="font-display text-3xl font-bold text-stone-900" data-testid="product-selling-price">{inr(unitPrice)}</span>
            {p.mrp > p.selling_price && <span className="text-lg text-stone-400 line-through" data-testid="product-mrp">{inr(p.mrp)}</span>}
            {off > 0 && <span className="text-sm font-semibold text-[#6A1B29]">{off}% off</span>}
          </div>
          {(p.club_price != null || p.wallet_reward > 0) && (
            <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="product-club-benefits">
              {p.club_price != null && <span className="inline-flex items-center gap-1.5 text-sm font-bold text-[#6A1B29] bg-[#FFF0F4] px-3 py-1.5 rounded-full">💗 CLUB {inr(p.club_price)}</span>}
              {p.wallet_reward > 0 && <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-700 bg-amber-50 px-3 py-1.5 rounded-full">🎁 {inr(p.wallet_reward)} Wallet</span>}
              {p.club_price != null && <Link to="/club" className="text-xs font-semibold text-stone-500 underline hover:text-[#6A1B29]" data-testid="product-join-club">Join Club to unlock →</Link>}
            </div>
          )}
          {hasShades && (
            <div className="mt-6" data-testid="shade-selector">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-stone-900">Select Shade{selectedVariant ? `: ${selectedVariant.name}` : ""}</p>
                {selectedVariant?.color_code && <span className="text-xs text-stone-400">{selectedVariant.color_code}</span>}
              </div>
              <div className="flex gap-3.5 mt-3 overflow-x-auto pb-2 -mx-1 px-1">
                {activeVariants.map((v) => {
                  const isOos = Number(v.stock) <= 0;
                  const sel = selectedVariant?.id === v.id;
                  return (
                    <button key={v.id} onClick={() => { setSelectedVariant(v); setActiveImg(0); }} title={v.name} aria-label={v.name}
                      data-testid={`shade-${v.id}`} className="relative shrink-0 flex flex-col items-center gap-1.5 focus:outline-none">
                      <span className={`w-11 h-11 rounded-full border transition-all ${sel ? "ring-2 ring-offset-2 ring-[#6A1B29] border-white" : "border-stone-300 hover:border-stone-400"} ${isOos ? "opacity-40" : ""}`}
                        style={{ background: v.secondary_color_code ? `linear-gradient(135deg, ${v.color_code} 0 50%, ${v.secondary_color_code} 50% 100%)` : (v.color_code || "#ccc") }} />
                      <span className={`text-[10px] max-w-[64px] truncate ${sel ? "text-stone-900 font-semibold" : "text-stone-500"}`}>{v.name}</span>
                    </button>
                  );
                })}
              </div>
              {shadeOos && <p className="text-xs text-red-600 mt-1" data-testid="shade-oos">This shade is out of stock</p>}
              {!selectedVariant && <p className="text-xs text-stone-400 mt-1">Please select a shade.</p>}
            </div>
          )}
          {!hasShades && (
          <p className="text-sm mt-2 text-stone-500">Status: <span className={(outlet ? p.outlet_in_stock : p.stock_status === "In Stock") ? "text-green-700 font-medium" : "text-red-600 font-medium"} data-testid="product-stock-status">{outlet ? (p.outlet_in_stock ? `In Stock at ${outlet.name}` : `Out of Stock at ${outlet.name}`) : p.stock_status}</span></p>
          )}
          {user?.role === "retail_owner" && p.retail_price && (
            <p className="text-sm mt-1 text-stone-500">Retail partner price: <span className="font-semibold text-stone-900" data-testid="product-retail-price">{inr(p.retail_price)}</span></p>
          )}
          <p className="text-stone-600 leading-relaxed mt-6" data-testid="product-description">{p.description}</p>
          <div className="flex gap-3 mt-8">
            <button onClick={addToBag} disabled={oos || shadeOos} data-testid="product-add-to-bag"
              className={`inline-flex items-center gap-2 text-white font-semibold px-6 py-3 rounded-full transition-colors ${(oos || shadeOos) ? "bg-stone-300 cursor-not-allowed" : "bg-[#6A1B29] hover:bg-[#501325]"}`}>
              {(oos || shadeOos) ? "Out of Stock" : "Add to Bag"}
            </button>
            <button onClick={wish} data-testid="product-wishlist-button"
              className={`inline-flex items-center gap-2 border font-semibold px-6 py-3 rounded-full transition-colors ${wished ? "border-[#6A1B29] text-[#6A1B29] bg-[#FFF0F4]" : "border-stone-900 text-stone-900 hover:bg-stone-900 hover:text-white"}`}>
              <Heart className={`w-4 h-4 ${wished ? "fill-[#6A1B29]" : ""}`} /> {wished ? "Wishlisted" : "Wishlist"}
            </button>
          </div>
          <div className="mt-8 border-t border-stone-200 pt-6 text-sm text-stone-500 space-y-1.5">
            <p>SKU: {variantSku || "—"}</p>
            <p>Category: {p.category}{p.subcategory ? ` / ${p.subcategory}` : ""}</p>
            <p>Ships across India.</p>
          </div>
        </div>
      </div>
      {reviews.length > 0 && (
        <section className="mt-14 border-t border-[#EADBD8] pt-10" data-testid="real-reviews">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[#231518]">Real Women, Real Experiences</h2>
          <p className="text-[#755C62] mt-1 text-sm">Reviews from Sakhis who bought this.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
            {reviews.map((rv) => (
              <div key={rv.id} className="bg-white border border-[#EADBD8] rounded-2xl overflow-hidden shadow-soft" data-testid={`review-${rv.id}`}>
                {rv.media?.[0] && <PostMedia src={rv.media[0]} className="w-full aspect-square object-cover" />}
                <div className="p-4">
                  <div className="flex items-center gap-2">
                    <span className="w-7 h-7 rounded-full bg-[#6A1B29] text-white grid place-items-center text-xs font-semibold">{rv.user_name?.[0] || "S"}</span>
                    <p className="text-sm font-semibold text-[#231518]">{rv.user_name}</p>
                    {rv.verified_purchase && <span className="text-[10px] font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded-full">Verified</span>}
                  </div>
                  {rv.rating > 0 && <div className="flex gap-0.5 mt-2">{[1, 2, 3, 4, 5].map((s) => <Star key={s} className={`w-3.5 h-3.5 ${s <= rv.rating ? "fill-[#6A1B29] text-[#6A1B29]" : "text-stone-200"}`} />)}</div>}
                  <p className="text-sm text-[#755C62] mt-2">{rv.caption}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CONTENT TABS */}
      <section className="mt-14 border-t border-[#EADBD8] pt-10" data-testid="product-tabs">
        <div className="flex gap-2 flex-wrap">
          {PTABS.map(([k, l]) => (
            <button key={k} data-testid={`ptab-${k}`} onClick={() => setTab(k)}
              className={`text-sm font-semibold px-5 py-2.5 rounded-full transition-colors ${tab === k ? "bg-[#6A1B29] text-white" : "bg-white border border-[#EADBD8] text-[#755C62] hover:border-[#6A1B29]"}`}>{l}</button>
          ))}
        </div>
        <div className="mt-6 max-w-3xl">
          {tab === "about" && <p className="text-[#755C62] leading-relaxed" data-testid="ptab-about-content">{p.description || "A thoughtfully chosen Suvikka find — beautiful, useful and easy on your pocket."}</p>}
          {tab === "why" && (
            <ul className="space-y-2.5" data-testid="ptab-why-content">
              {whyPoints.map((t, i) => (<li key={i} className="flex items-start gap-2.5 text-[#755C62]"><span className="text-[#D4AF37] mt-0.5">✦</span> {t}</li>))}
            </ul>
          )}
          {tab === "details" && (
            <div className="text-[#755C62] space-y-1.5" data-testid="ptab-details-content">
              <p>SKU: {variantSku || "—"}</p>
              <p>Category: {p.category}{p.subcategory ? ` / ${p.subcategory}` : ""}</p>
              <p>Ships across India.</p>
            </div>
          )}
        </div>
      </section>

      {data.related.length > 0 && (
        <section className="mt-14" data-testid="complete-the-look">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[#231518]">Complete The Look</h2>
          <p className="text-[#755C62] mt-1 text-sm">Pieces that pair beautifully with this.</p>
          <div className="flex gap-4 mt-6 overflow-x-auto pb-3 scrollbar-hide">
            {data.related.slice(0, 8).map((r) => (
              <Link key={r.id} to={`/product/${r.slug || r.id}`} data-testid={`ctl-${r.id}`} className="shrink-0 w-40 sm:w-48 group">
                <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-[#F9ECE8] shadow-soft">{r.images?.[0] && <img src={r.images[0]} alt={r.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}</div>
                <h3 className="text-sm font-medium text-[#231518] mt-2 line-clamp-1">{r.title}</h3>
                <p className="text-sm font-semibold text-[#6A1B29]">{inr(r.selling_price)}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="mt-16" data-testid="see-it-on-sakhis">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#6A1B29]">How Sakhis Are Styling It</p>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-stone-900 mt-1">💗 Seen On Sakhis</h2>
          </div>
          <Link to="/share-look" className="text-sm font-semibold text-[#6A1B29]" data-testid="pdp-post-look">Show your look →</Link>
        </div>
        {looks.length === 0 ? (
          <p className="text-sm text-stone-500 mt-4 bg-stone-50 border border-stone-200 rounded-2xl p-8 text-center" data-testid="pdp-looks-empty">Be the first Sakhi to show this look ✨</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 mt-6" data-testid="pdp-looks-grid">
            {looks.map((lk) => (
              <div key={lk.id} className="group" data-testid={`pdp-look-${lk.id}`}>
                <Link to={lk.username ? `/sakhi/${lk.username}` : "/sakhi-wall"} className="block relative rounded-2xl overflow-hidden bg-stone-100 aspect-[4/5]">
                  {lk.media?.[0] && <PostMedia src={lk.media[0]} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-3 text-white">
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-[#6A1B29] grid place-items-center text-[11px] font-semibold shrink-0">{lk.user_name?.[0] || "S"}</span>
                      <p className="text-xs font-semibold truncate">{lk.user_name}</p>
                    </div>
                    {lk.caption && <p className="text-[11px] text-white/90 line-clamp-1 mt-1">{lk.caption}</p>}
                  </div>
                </Link>
                {lk.products?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`pdp-look-shop-${lk.id}`}>
                    {lk.products.slice(0, 3).map((pr) => (
                      <Link key={pr.id} to={`/product/${pr.slug || pr.id}`}
                        className={`inline-flex items-center gap-1.5 rounded-full pl-1 pr-2.5 py-1 border text-[11px] font-semibold transition-colors ${pr.id === p.id ? "border-[#6A1B29] bg-[#FFF0F4] text-[#6A1B29]" : "border-stone-200 bg-white text-stone-700 hover:border-[#6A1B29]"}`}>
                        <img src={pr.image} alt="" className="w-5 h-5 rounded-full object-cover" />
                        {pr.id === p.id ? "This item" : inr(pr.selling_price)}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
      {data.related.length > 0 && (
        <div className="mt-16">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-stone-900">You May Also Like</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mt-6">
            {data.related.map((r, i) => <ProductCard key={r.id} product={r} index={i} />)}
          </div>
        </div>
      )}
    </div>
  );
}
